import { debugLog } from './debugLog';
import { supabase } from '../lib/supabase';

const DEBOUNCE_MS = 800;
const debounceTimers = {};
const pendingPushes = {};

// ── Transparent gzip compression for cloud payloads ──────────────────────────
// Large holdings/history blobs were timing out Postgres on upsert ("canceling
// statement due to statement timeout"). We gzip the JSON string before storing
// it (≈10–20× smaller for repetitive financial data) and decompress on pull.
// Values are tagged with a "gz:" prefix so old uncompressed rows still load.
const GZIP_PREFIX = 'gz:';
const _canCompress =
  typeof CompressionStream !== 'undefined' &&
  typeof DecompressionStream !== 'undefined' &&
  typeof Blob !== 'undefined';

function _bytesToB64(bytes) {
  let bin = '';
  const CHUNK = 0x8000; // avoid call-stack overflow on large arrays
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

function _b64ToBytes(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

async function gzipToB64(str) {
  if (!_canCompress || typeof str !== 'string') return str;
  try {
    const stream = new Blob([str]).stream().pipeThrough(new CompressionStream('gzip'));
    const buf = await new Response(stream).arrayBuffer();
    return GZIP_PREFIX + _bytesToB64(new Uint8Array(buf));
  } catch (e) {
    console.warn('[CloudSync] gzip failed, storing raw:', e?.message);
    return str;
  }
}

async function gunzipFromB64(value) {
  if (typeof value !== 'string' || !value.startsWith(GZIP_PREFIX)) return value;
  if (!_canCompress) {
    console.error('[CloudSync] received gzipped value but no DecompressionStream');
    return value;
  }
  const bytes = _b64ToBytes(value.slice(GZIP_PREFIX.length));
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'));
  return await new Response(stream).text();
}

let onPushError = null;
let onPushSuccess = null;

export function setSyncCallbacks({ onError, onSuccess } = {}) {
  onPushError = onError || null;
  onPushSuccess = onSuccess || null;
}

// מזהה המשתמש מה-session המקומי (כולל רענון טוקן שקוף) — בלי round-trip
// ל-/auth/v1/user בכל דחיפה. getUser() נכשל ב-403 כשה-session בוטל בצד השרת
// (למשל התנתקות global ממכשיר אחר) והקוד הישן חזר בשקט בלי user — הדחיפה
// "הצליחה" לכאורה, הממשק סימן מסונכרן, והנתונים מעולם לא הגיעו לענן.
// עכשיו: אין session ⇒ נזרקת שגיאה ⇒ _setSyncError ⇒ המשתמש רואה חיווי אדום.
async function requireUserId() {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw error;
  const userId = session?.user?.id;
  if (!userId) throw new Error('ההתחברות פגה — יש להתחבר מחדש כדי לסנכרן');
  return userId;
}

export async function pushKey(key, value) {
  const userId = await requireUserId();

  const stored = await gzipToB64(value);
  const { error } = await supabase
    .from('user_data')
    .upsert(
      { user_id: userId, key, value: stored, updated_at: new Date().toISOString() },
      { onConflict: 'user_id,key' }
    );

  if (error) {
    console.error('[CloudSync] pushKey failed:', key, error.message);
    throw error;
  }
}

export async function pushAll(data, storageKeys) {
  const userId = await requireUserId();

  const keys = storageKeys.filter(key => data[key] != null);
  const rows = await Promise.all(keys.map(async key => ({
    user_id: userId,
    key,
    value: await gzipToB64(data[key]),
    updated_at: new Date().toISOString(),
  })));

  if (rows.length === 0) return;

  const { error } = await supabase
    .from('user_data')
    .upsert(rows, { onConflict: 'user_id,key' });

  if (error) {
    console.error('[CloudSync] pushAll failed:', error.message);
    throw error;
  }
  debugLog('[CloudSync] pushAll complete:', rows.length, 'keys');
}

export async function pullAll() {
  const userId = await requireUserId();

  const { data, error } = await supabase
    .from('user_data')
    .select('key, value, updated_at')
    .eq('user_id', userId);

  if (error) {
    console.error('[CloudSync] pullAll failed:', error.message);
    throw error;
  }

  const result = {};
  for (const row of (data || [])) {
    result[row.key] = { value: await gunzipFromB64(row.value), updated_at: row.updated_at };
  }
  debugLog('[CloudSync] pullAll complete:', Object.keys(result).length, 'keys');
  return result;
}

export async function deleteAll() {
  const userId = await requireUserId();

  const { error } = await supabase
    .from('user_data')
    .delete()
    .eq('user_id', userId);

  if (error) {
    console.error('[CloudSync] deleteAll failed:', error.message);
    throw error;
  }
  debugLog('[CloudSync] deleteAll complete');
}

export function debouncedPush(key, value) {
  if (debounceTimers[key]) {
    clearTimeout(debounceTimers[key]);
  }
  pendingPushes[key] = value;
  debounceTimers[key] = setTimeout(async () => {
    delete debounceTimers[key];
    delete pendingPushes[key];
    try {
      await pushKey(key, value);
      onPushSuccess?.();
    } catch (err) {
      // AbortError = ה-fetch נחתך ע"י ה-timeout (payload גדול / רשת איטית) —
      // ניסיון חוזר יחיד אחרי השהיה לפני שמדווחים שגיאה.
      if (err?.name === "AbortError") {
        console.warn("[CloudSync] push aborted, retrying once:", key);
        setTimeout(async () => {
          try {
            await pushKey(key, value);
            onPushSuccess?.();
          } catch (err2) {
            onPushError?.(err2);
          }
        }, 2000);
        return;
      }
      onPushError?.(err);
    }
  }, DEBOUNCE_MS);
}

export function flushPending() {
  for (const key of Object.keys(debounceTimers)) {
    clearTimeout(debounceTimers[key]);
    delete debounceTimers[key];
  }
  const keys = Object.keys(pendingPushes);
  for (const key of keys) {
    const value = pendingPushes[key];
    delete pendingPushes[key];
    pushKey(key, value)
      .then(() => onPushSuccess?.())
      .catch(err => onPushError?.(err));
  }
  return keys.length;
}

export function hasPendingPushes() {
  return Object.keys(pendingPushes).length > 0;
}

export function cancelPendingForKey(key) {
  if (debounceTimers[key]) {
    clearTimeout(debounceTimers[key]);
    delete debounceTimers[key];
  }
  delete pendingPushes[key];
}

export function cancelAllPending() {
  for (const key of Object.keys(debounceTimers)) {
    clearTimeout(debounceTimers[key]);
    delete debounceTimers[key];
  }
  for (const key of Object.keys(pendingPushes)) {
    delete pendingPushes[key];
  }
}
